import {
  NOT_CRAWLER_SCOPED,
  type DetectorCatalogResponse,
  type IncidentDetailResponse,
} from '@prerender/diagnostics-api';
import type { ReactNode } from 'react';
import { Alert } from 'react-bootstrap';

import Loading from '../../app/components/Loading';
import AffectedUrls from './AffectedUrls';
import ClosedBanner from './ClosedBanner';
import CrawlerEvidence from './CrawlerEvidence';
import DiagnosticsPage from './DiagnosticsPage';
import IncidentDetailRail from './IncidentDetailRail';
import IncidentDomains from './IncidentDomains';
import IncidentHistory from './IncidentHistory';
import IncidentNarrative from './IncidentNarrative';
import { incidentHeadline } from './incidentHeadline';
import { narrativeFor, narrativeSubjectOf } from './incidentNarratives';
import { incidentDetailErrorMessage } from './incidentsErrorMessage';
import type { Loadable } from './loadable';
import OriginBlockingEvidence from './OriginBlockingEvidence';
import { incidentBlockingFiguresOf, ORIGIN_BLOCKING_INCIDENT_TYPE, rateRulesOf } from './originBlockingFigures';
import { DIAGNOSTICS_ROUTE } from './routes';

import './diagnostics.css';

type IncidentDetailsViewProps = {
  reference: string;
  onBackToTriage?: () => void;
  incident: Loadable<IncidentDetailResponse>;
  catalog: Loadable<DetectorCatalogResponse>;
  now: Date;
  actions: (incident: IncidentDetailResponse) => ReactNode;
};

function IncidentDetailsView({
  reference,
  onBackToTriage,
  incident,
  catalog,
  now,
  actions,
}: IncidentDetailsViewProps): JSX.Element {
  const backToTriage = { to: DIAGNOSTICS_ROUTE, label: 'Back to triage', onFollow: onBackToTriage };

  if (incident.status !== 'loaded') {
    return (
      <DiagnosticsPage title={reference} subtitle={null} back={backToTriage}>
        {incident.status === 'failed' ? (
          <Alert variant="danger">{incidentDetailErrorMessage(incident.error, reference)}</Alert>
        ) : (
          <Loading text="Loading incident" />
        )}
      </DiagnosticsPage>
    );
  }

  const detail = incident.data;
  const subject = narrativeSubjectOf(detail);
  const narrative = subject === null ? null : narrativeFor(subject);
  const blockingFigures =
    detail.incidentType === ORIGIN_BLOCKING_INCIDENT_TYPE ? incidentBlockingFiguresOf(detail.domains) : null;
  const rateRules = rateRulesOf(catalog, detail.incidentType);
  const evidence = blockingFigures !== null && (
    <OriginBlockingEvidence incident={detail} figures={blockingFigures} rules={rateRules} />
  );

  return (
    <DiagnosticsPage
      title={incidentHeadline({ ...detail, headlineDomainCount: detail.affectedDomainCount })}
      subtitle={null}
      back={backToTriage}
    >
      {detail.status === 'closed' && <ClosedBanner closedReason={detail.closedReason} />}
      <div className="diagnostics-detail-card">
        <div className="diagnostics-detail-main">
          {narrative === null ? evidence : <IncidentNarrative narrative={narrative} evidence={evidence} />}
          {detail.crawlerCategory !== NOT_CRAWLER_SCOPED && (
            <CrawlerEvidence crawlerEvidence={detail.crawlerEvidence} />
          )}
        </div>
        <IncidentDetailRail incident={detail} now={now} actions={actions(detail)} />
      </div>
      <IncidentDomains domains={detail.domains} incidentType={detail.incidentType} rules={rateRules} />
      <IncidentHistory history={detail.history} now={now} />
      <AffectedUrls domains={detail.domains} />
    </DiagnosticsPage>
  );
}

export default IncidentDetailsView;
